import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconRegistryService } from './icon-registry.service';
import { getIconSize, IconSize, IconWeight, WEIGHT_SUFFIXES } from './icon.types';
import type { IconName as FormaIconName } from './icon.types';

@Component({
  selector: 'icon',
  templateUrl: './icon.html',
  styleUrls: ['./icon.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'forma-icon',
    '[attr.data-icon-name]': 'name()',
    '[attr.role]': 'role()',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.aria-hidden]': 'ariaHidden()',
    '[class.forma-icon--small]': 'size() === "small"',
    '[class.forma-icon--medium]': 'size() === "medium"',
    '[class.forma-icon--large]': 'size() === "large"',
  },
})
export class Icon {
  /** Name must be registered in IconRegistryService */
  name = input.required<FormaIconName>();

  /** The size of the icon - can be a preset size or a number in pixels */
  size = input<IconSize>('medium');

  weight = input<IconWeight>('bold');

  color = input<string>('currentColor');

  /** Accessible label for screen readers (required if not decorative) */
  ariaLabel = input<string | undefined>(undefined);

  /** Whether the icon is purely decorative (hidden from screen readers) */
  ariaHidden = input<boolean>(false);

  protected role = computed(() => {
    return this.ariaHidden() ? null : 'img';
  });

  protected numericSize = computed(() => getIconSize(this.size()));

  protected svgContent = signal<SafeHtml>('');

  constructor(
    private iconRegistry: IconRegistryService,
    private elementRef: ElementRef<HTMLElement>,
    private sanitizer: DomSanitizer,
  ) {
    effect(() => {
      const iconName = this.name();
      const weight = this.weight();

      const hasWeightSuffix = WEIGHT_SUFFIXES.some((s) => iconName?.endsWith(s));
      const isRegularSuffix = iconName?.endsWith('-regular');
      const key = isRegularSuffix
        ? iconName.slice(0, -'-regular'.length)
        : hasWeightSuffix
          ? iconName
          : weight === ('regular' as IconWeight)
            ? iconName
            : `${iconName}-${weight}`;

      const svgData = this.iconRegistry.getIcon(key);
      if (svgData) {
        this.svgContent.set(this.sanitizer.bypassSecurityTrustHtml(svgData));
      } else {
        console.warn(`Icon "${key}" not found in registry. Did you forget to register it?`);
        this.svgContent.set('');
      }
    });

    effect(() => {
      const element = this.elementRef.nativeElement;
      element.style.setProperty(
        '--icon-size',
        this.numericSize() !== undefined ? `${this.numericSize()}` : '1em',
      );
      element.style.setProperty('--icon-color', this.color());
    });
  }
}
